"use client";

import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Check, Loader2, ThumbsDown, ThumbsUp, Wand2 } from "lucide-react";
import { useTranslations } from "next-intl";
import { toast } from "sonner";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { sendFixErrorFeedback } from "../api";
import { useFixError } from "../hooks";
import { playgroundFixErrorMessageKey } from "../lib/api-error-message";
import { createFixOperationKey, shouldReuseFixOperationKey } from "../lib/fix-operation";
import { collapseUnchanged, diffSnippetSources } from "../lib/snippet-diff";
import type { CodeFixResult, FixErrorInput } from "../types";

type FixErrorModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The request, assembled by the parent from the run that failed. */
  request: Omit<FixErrorInput, "idempotencyKey"> | null;
  /** The buffer as it is right now, so a stale suggestion can be caught. */
  currentSource: string;
  /** Applies the fix as one undoable editor edit; `false` when nothing changed. */
  onApplyFix: (patchedSource: string) => boolean;
  /**
   * `EX-HNT01`: ask another route with the same answer (the practice page's,
   * which reads the program from the submission). When the answer names the
   * program it fixed (`sourceCode`), that is what a stale suggestion is
   * measured against.
   */
  ask?: (input: FixErrorInput) => Promise<CodeFixResult & { sourceCode?: string }>;
};

/**
 * Fix My Error (`PG-AI02`, ADR-0051).
 *
 * The order on screen is the product decision: cause, explanation and steps
 * first, the suggested fix behind a deliberate "Show the fix", and applying it
 * behind one more click. A student who is handed a corrected file and no reason
 * has been unblocked and taught nothing, which is the outcome the task exists
 * to avoid.
 *
 * Applying follows the version-restore path exactly (`PG-IDE05`): one undoable
 * editor edit, no auto-save, and the toast says Ctrl+Z undoes it. It adds the
 * guard a restore does not need: this suggestion was computed against the
 * buffer as it stood when the request went out, so if the student has typed
 * since, Accept refuses rather than overwriting the newer work.
 */
export function FixErrorModal({
  open,
  onOpenChange,
  request,
  currentSource,
  onApplyFix,
  ask,
}: FixErrorModalProps) {
  const t = useTranslations("playground");
  const dialogRef = useRef<HTMLDialogElement>(null);
  const operationRef = useRef<FixErrorInput | null>(null);
  const headingId = useId();
  const mutation = useFixError(ask);
  const data: (CodeFixResult & { sourceCode?: string }) | undefined = mutation.data;

  const [patchShown, setPatchShown] = useState(false);
  const [applied, setApplied] = useState(false);
  const [rated, setRated] = useState<boolean | null>(null);

  const errorKey = playgroundFixErrorMessageKey(mutation.error);
  const errorMessage = errorKey
    ? t(errorKey)
    : mutation.error instanceof ApiError
      ? mutation.error.message
      : null;

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (open) {
      if (!el.open) el.showModal();
    } else if (el.open) {
      el.close();
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      operationRef.current = null;
      return;
    }
    if (operationRef.current) return;
    mutation.reset();
    setPatchShown(false);
    setApplied(false);
    setRated(null);
    if (!request) return;
    const operation: FixErrorInput = { ...request, idempotencyKey: createFixOperationKey() };
    operationRef.current = operation;
    mutation.mutate(operation);
    // Deliberately on open only: the request is the run that failed, and it
    // must not change under a student who is reading the answer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /**
   * The suggestion is only offerable while the buffer still matches the source
   * it was computed from. Editing invalidates it, which the UI says out loud
   * rather than applying an edit against text the student has since changed.
   */
  const fixedSource = data?.sourceCode ?? operationRef.current?.sourceCode;
  const stale = Boolean(data?.patchedSource && fixedSource !== undefined && fixedSource !== currentSource);

  const diff = useMemo(
    () => (data?.patchedSource ? diffSnippetSources(currentSource, data.patchedSource) : null),
    [data?.patchedSource, currentSource],
  );
  const rows = useMemo(() => (diff ? collapseUnchanged(diff.lines) : []), [diff]);

  function handleRetry() {
    const previous = operationRef.current;
    if (!previous) return;
    const operation = shouldReuseFixOperationKey(mutation.error)
      ? previous
      : { ...previous, idempotencyKey: createFixOperationKey() };
    operationRef.current = operation;
    mutation.reset();
    mutation.mutate(operation);
  }

  function handleApply() {
    const patched = data?.patchedSource;
    const operation = operationRef.current;
    if (!patched || !operation || stale) return;
    if (!onApplyFix(patched)) {
      toast.info(t("fixErrorPatchUnchanged"));
      return;
    }
    setApplied(true);
    toast.success(t("fixErrorPatchApplied"));
    void sendFixErrorFeedback({
      idempotencyKey: operation.idempotencyKey,
      patchAccepted: true,
    }).catch(() => undefined);
  }

  function handleRate(helpful: boolean) {
    const operation = operationRef.current;
    if (!operation || rated !== null) return;
    setRated(helpful);
    void sendFixErrorFeedback({ idempotencyKey: operation.idempotencyKey, helpful }).catch(
      () => undefined,
    );
  }

  return (
    <dialog
      ref={dialogRef}
      className="w-[min(100vw-2rem,640px)] max-h-[min(90vh,760px)] rounded-xl border border-white/15 bg-[#141416] p-0 text-white shadow-2xl backdrop:bg-black/60"
      onClose={() => onOpenChange(false)}
      aria-labelledby={headingId}
    >
      <div className="flex max-h-[min(90vh,760px)] flex-col gap-4 p-5">
        <div className="flex shrink-0 items-start justify-between gap-3">
          <h2 id={headingId} className="flex items-center gap-2 text-lg font-semibold text-white">
            <Wand2 className="h-5 w-5 shrink-0 text-[#FFAB8F]" aria-hidden />
            {t("fixErrorModalTitle")}
          </h2>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded-lg border border-white/15 px-3 py-1.5 text-sm text-white/85 transition hover:bg-white/10"
          >
            {t("fixErrorModalClose")}
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {mutation.isPending && (
            <div className="flex flex-col items-center justify-center gap-3 py-12 text-white/55">
              <Loader2 className="h-8 w-8 animate-spin text-[#E94D1D]" aria-hidden />
              <span className="text-sm">{t("fixErrorLoading")}</span>
            </div>
          )}

          {mutation.isError && !mutation.isPending && (
            <div role="alert" className="space-y-4 rounded-xl border border-red-400/25 bg-red-400/5 p-4">
              <p className="text-sm text-red-300">{errorMessage ?? t("fixErrorFailed")}</p>
              <button
                type="button"
                onClick={handleRetry}
                className="min-h-11 rounded-lg border border-white/15 px-4 text-sm font-medium text-white transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E94D1D]"
              >
                {t("fixErrorRetry")}
              </button>
            </div>
          )}

          {mutation.isSuccess && data && !mutation.isPending && (
            <div className="space-y-6 text-sm text-white/90">
              <section>
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-white/50">
                  {t("fixErrorCause")}
                </h3>
                <p className="leading-relaxed text-white/90">{data.cause}</p>
              </section>

              <section>
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-white/50">
                  {t("fixErrorExplanation")}
                </h3>
                <p className="whitespace-pre-wrap leading-relaxed text-white/80">
                  {data.explanation}
                </p>
              </section>

              <section>
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-white/50">
                  {t("fixErrorSteps")}
                </h3>
                <ol className="space-y-3">
                  {data.steps.map((step, i) => (
                    <li
                      key={`${step.title}-${i}`}
                      className="rounded-lg border border-white/10 bg-white/[0.03] p-3"
                    >
                      <p className="mb-1 font-medium text-white/90">
                        {i + 1}. {step.title}
                      </p>
                      <p className="whitespace-pre-wrap text-white/75">{step.detail}</p>
                    </li>
                  ))}
                </ol>
              </section>

              {data.pitfalls.length > 0 && (
                <section>
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-white/50">
                    {t("fixErrorPitfalls")}
                  </h3>
                  <ul className="list-disc space-y-1.5 pl-5 text-white/80">
                    {data.pitfalls.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </section>
              )}

              <section className="border-t border-white/10 pt-4">
                {!data.patchedSource ? (
                  <p className="text-xs leading-relaxed text-white/50">{t("fixErrorPatchNone")}</p>
                ) : !patchShown ? (
                  <button
                    type="button"
                    onClick={() => setPatchShown(true)}
                    className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-white/15 bg-white/5 px-4 text-sm font-medium text-white transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E94D1D]"
                  >
                    <Wand2 className="h-4 w-4 text-[#FFAB8F]" aria-hidden />
                    {t("fixErrorShowPatch")}
                  </button>
                ) : (
                  <>
                    <p className="mb-1.5 text-xs text-white/55">
                      {diff?.toobig
                        ? t("fixErrorPatchTooBig")
                        : t("fixErrorPatchSummary", {
                            added: diff?.added ?? 0,
                            removed: diff?.removed ?? 0,
                          })}
                    </p>
                    {diff && !diff.toobig && (
                      <div className="max-h-[min(32vh,240px)] overflow-auto rounded-lg border border-white/10 bg-[#0B0B0C] p-2 font-mono text-[11px] leading-relaxed">
                        {rows.map((row, index) =>
                          row.kind === "gap" ? (
                            <p key={`gap-${index}`} className="select-none py-1 text-white/25">
                              {t("fixErrorPatchHidden", { n: row.hidden })}
                            </p>
                          ) : (
                            <p
                              key={`${row.kind}-${index}`}
                              className={cn(
                                "whitespace-pre-wrap break-all",
                                row.kind === "added" && "bg-emerald-500/10 text-emerald-200",
                                row.kind === "removed" && "bg-red-500/10 text-red-200",
                                row.kind === "same" && "text-white/45",
                              )}
                            >
                              <span className="select-none pr-2 text-white/25" aria-hidden>
                                {row.kind === "added" ? "+" : row.kind === "removed" ? "-" : " "}
                              </span>
                              {row.text || " "}
                            </p>
                          ),
                        )}
                      </div>
                    )}
                    {stale ? (
                      <p role="status" className="mt-3 text-xs leading-relaxed text-amber-200/90">
                        {t("fixErrorPatchStale")}
                      </p>
                    ) : (
                      <button
                        type="button"
                        onClick={handleApply}
                        disabled={applied}
                        className="mt-3 inline-flex min-h-11 items-center gap-2 rounded-lg bg-gradient-to-r from-[#E94D1D] via-[#f05a28] to-[#FFAB8F] px-4 text-sm font-semibold text-white shadow-lg transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {applied ? (
                          <Check className="h-4 w-4" aria-hidden />
                        ) : (
                          <Wand2 className="h-4 w-4" aria-hidden />
                        )}
                        {applied ? t("fixErrorPatchAppliedLabel") : t("fixErrorApplyPatch")}
                      </button>
                    )}
                  </>
                )}
              </section>

              <section className="flex items-center gap-2 border-t border-white/10 pt-4">
                <span className="text-xs text-white/50">{t("fixErrorHelpfulQuestion")}</span>
                <button
                  type="button"
                  onClick={() => handleRate(true)}
                  disabled={rated !== null}
                  aria-label={t("fixErrorHelpfulYes")}
                  className={cn(
                    "inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/15 transition hover:bg-white/10 disabled:cursor-not-allowed",
                    rated === true ? "border-emerald-400/40 text-emerald-300" : "text-white/60",
                  )}
                >
                  <ThumbsUp className="h-4 w-4" aria-hidden />
                </button>
                <button
                  type="button"
                  onClick={() => handleRate(false)}
                  disabled={rated !== null}
                  aria-label={t("fixErrorHelpfulNo")}
                  className={cn(
                    "inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/15 transition hover:bg-white/10 disabled:cursor-not-allowed",
                    rated === false ? "border-amber-400/40 text-amber-200" : "text-white/60",
                  )}
                >
                  <ThumbsDown className="h-4 w-4" aria-hidden />
                </button>
                {rated !== null && (
                  <span role="status" className="text-xs text-white/45">
                    {t("fixErrorHelpfulThanks")}
                  </span>
                )}
              </section>
            </div>
          )}
        </div>
      </div>
    </dialog>
  );
}
